import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { db } from "@/lib/supabase";
import type { Database } from "@/types/database.types";
import { useRealtimeRefresh } from "@/hooks/useRealtimeRefresh";

export type Category = Database["public"]["Tables"]["categories"]["Row"];

export interface UseCategoriesResult {
  /** All categories, ordered by sort_order ascending (board section order). */
  categories: Category[];
  byId: Map<string, Category>;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const CategoriesContext = createContext<UseCategoriesResult>({
  categories: [],
  byId: new Map(),
  loading: true,
  error: null,
  refresh: async () => {},
});

export function CategoriesProvider({ children }: { children: React.ReactNode }) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error } = await db.categories.getAll();
    if (error) {
      setError(error.message ?? "Failed to load categories");
    } else {
      setCategories(data ?? []);
      setError(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // The list is small; a non-silent refresh on remote change won't visibly flicker.
  useRealtimeRefresh(["categories"], refresh);

  const value = useMemo<UseCategoriesResult>(() => {
    const byId = new Map(categories.map((c) => [c.id, c]));
    return {
      categories,
      byId,
      loading,
      error,
      refresh,
    };
  }, [categories, loading, error, refresh]);

  return <CategoriesContext.Provider value={value}>{children}</CategoriesContext.Provider>;
}

export function useCategories(): UseCategoriesResult {
  return useContext(CategoriesContext);
}
